import * as t from 'io-ts';

import {makeSafeArrayType} from '../../../../libs/shared/core/src/models/api/types/arrayTypes';

export const RRefilterableItem = t.readonly(
  t.type({
    edgeNgramTokens: makeSafeArrayType(t.string),
  }),
);

export interface RefilterableItem extends t.TypeOf<typeof RRefilterableItem> {}

export function refilterWithLimit<T extends RefilterableItem>(
  refilterableItems: ReadonlyArray<T>,
  queryTokens: ReadonlyArray<string>,
  maximumNumberOfResults: number,
) {
  return filterWithLimit(
    refilterableItems,
    (item) => isRefilterableItemMatchingQuery(item, queryTokens),
    maximumNumberOfResults,
  );
}

export function refilterNonMatching<T extends RefilterableItem>(
  refilterableItems: ReadonlyArray<T>,
  queryTokens: ReadonlyArray<string>,
) {
  return refilterableItems.filter((item) => !isRefilterableItemMatchingQuery(item, queryTokens));
}

function areTokensMatchingQueryEdgeNgram(
  itemTokens: ReadonlyArray<string>,
  queryTokens: ReadonlyArray<string>,
) {
  for (const queryToken of queryTokens) {
    // Importantly, since queryToken can be the empty string, startsWith('') always returns true.
    if (!itemTokens.some((itemToken) => itemToken.startsWith(queryToken))) {
      return false;
    }
  }

  return true;
}

function isRefilterableItemMatchingQuery(item: RefilterableItem, queryTokens: ReadonlyArray<string>) {
  return areTokensMatchingQueryEdgeNgram(item.edgeNgramTokens, queryTokens);
}

// This returns less than `limit` (or equal) elements of `array`, that match `predicate`.
// It is useful if the predicate is costly and you want to minimize the number of invocations.
function filterWithLimit<T>(
  array: ReadonlyArray<T>,
  predicate: (e: T) => boolean,
  limit: number,
): ReadonlyArray<T> {
  if (limit < 1) {
    return [];
  }

  if (limit >= array.length) {
    return array.filter(predicate);
  }

  const output = [];

  for (let i = 0; i < array.length; i++) {
    if (predicate(array[i])) {
      output.push(array[i]);

      // The following takes into account non-integer limits.
      // e.g. if limit is 1.5 we should not return two elements.
      if (output.length + 1 > limit) {
        break;
      }
    }
  }

  return output;
}
